<template>
  <div>
    <dte-full-page-banner-sheet
      v-if="showFullPageBanner"
      ref="bannerPage"
      :document-data="documentData"
      layout="full"
      :page-number="1"
      :total-pages="totalPageCount"
    />

    <article
      v-for="page in pages"
      :key="`full-${displayPageNumber(page.number)}`"
      ref="pages"
      class="dte-sheet dte-sheet--full"
    >
      <dte-page-header-section
        v-if="page.isFirst"
        :document-data="documentData"
      />

      <div class="dte-spacer" v-if="page.showReceiver"></div>
      <dte-recipient-section
        v-if="page.showReceiver"
        :document-data="documentData"
      />

      <div class="dte-spacer" v-if="page.showRelated || page.showAssociated"></div>
      <dte-related-documents-section
        v-if="page.showRelated"
        title="Documentos Relacionados"
        :documents="documentData.relatedDocuments"
      />

      <div class="dte-spacer" v-if="page.showRelated && page.showAssociated"></div>
      <dte-related-documents-section
        v-if="page.showAssociated"
        title="Documentos Asociados"
        :documents="documentData.associatedDocuments"
      />

      <div class="dte-spacer"></div>
      <dte-detail-table-section
        :class="['dte-detail-table-section--full', detailTableDensityClass]"
        :document-data="documentData"
        :page="page"
      />

      <template v-if="page.showTail">
        <div class="dte-spacer"></div>
        <div
          class="dte-summary"
          :class="{ 'dte-summary--single': !(documentData.notes || documentData.showLargeAmountResponsibles) }"
        >
          <dte-totals-section :document-data="documentData" />
          <dte-footer-section
            v-if="documentData.notes || documentData.showLargeAmountResponsibles"
            :document-data="documentData"
          />
        </div>

        <div class="dte-spacer" v-if="appendixRows.length"></div>
        <section
          v-if="appendixRows.length"
          class="dte-card dte-detail-block dte-detail-table-section--full"
        >
          <div class="dte-card__body" style="padding: 0;">
            <table class="dte-table dte-table--detail" style="table-layout: fixed;">
              <colgroup>
                <col style="width: 20%;" />
                <col style="width: 35%;" />
                <col style="width: 45%;" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="3" class="dte-table__center">APENDICE</th>
                </tr>
                <tr>
                  <th>Campo</th>
                  <th>Etiqueta</th>
                  <th>Valor</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in appendixRows"
                  :key="`appendix-${index}`"
                >
                  <td>{{ row.campo }}</td>
                  <td>{{ row.etiqueta }}</td>
                  <td>{{ row.valor }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <GuaranteePrintBlock
          v-if="page.showTail && documentData.showGuaranteeInline && documentData.guaranteeHtml"
          :html="documentData.guaranteeHtml"
          :business-id="guaranteeBusinessId"
          :branch-id="guaranteeBranchId"
          guarantee-type="COMPROBANTE"
          font-size="11px"
        />

      </template>

      <div class="dte-sheet__page-number">
        Pag. {{ displayPageNumber(page.number) }} / {{ totalPageCount }}
      </div>
    </article>
    <article
      class="dte-sheet dte-sheet--full"
      v-if="showGuaranteePage"
    >
      <div class="dte-spacer"></div>
      <GuaranteePrintBlock
        :business-id="guaranteeBusinessId"
        :branch-id="guaranteeBranchId"
        guarantee-type="COMPROBANTE"
        font-size="11px"
      />
      <div class="dte-sheet__page-number">
        Pag. {{ totalPageCount }} / {{ totalPageCount }}
      </div>
    </article>
  </div>
</template>

<script>
import DteDetailTableSection from "../sections/DteDetailTableSection.vue";
import DteFooterSection from "../sections/DteFooterSection.vue";
import DteFullPageBannerSheet from "../sections/DteFullPageBannerSheet.vue";
import DtePageHeaderSection from "../sections/DtePageHeaderSection.vue";
import DteRecipientSection from "../sections/DteRecipientSection.vue";
import DteRelatedDocumentsSection from "../sections/DteRelatedDocumentsSection.vue";
import DteTotalsSection from "../sections/DteTotalsSection.vue";
import GuaranteePrintBlock from "@/components/ReportsAndPrintings/GuaranteePrintBlock.vue";
import { isDteTemplateFieldVisible } from "@/helpers/dteTemplateSettings";
export default {
  name: "PrintPosDteLetterFullLayout",
  components: {
    DteDetailTableSection,
    DteFooterSection,
    DteFullPageBannerSheet,
    DtePageHeaderSection,
    DteRecipientSection,
    DteRelatedDocumentsSection,
    DteTotalsSection,
    GuaranteePrintBlock,
  },
  props: {
    documentData: {
      type: Object,
      required: true,
    },
    pages: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    appendixRows() {
      const rows = Array.isArray(this.documentData.appendices)
        ? this.documentData.appendices
        : [];

      return rows.map((row) => ({
        campo: row.campo,
        etiqueta: row.etiqueta,
        valor: row.valor,
      }));
    },
    templateSettings() {
      return this.documentData.templateSettings || {};
    },
    showFullPageBanner() {
      return Boolean(this.documentData.showFullPageBanner);
    },
    totalPageCount() {
      return this.pages.length + (this.showFullPageBanner ? 1 : 0) + (this.showGuaranteePage ? 1 : 0);
    },
    showGuaranteePage() {
      return Boolean(this.documentData.guaranteeHtml) && !this.documentData.showGuaranteeInline;
    },
    detailColumnCount() {
      const items = this.documentData.items || [];
      const invoiceType =
        this.documentData &&
        this.documentData.meta &&
        this.documentData.meta.invoiceType;
      let count = 5;

      if (this.isDetailFieldVisible("itemType")) {
        count += 1;
      }

      if (
        this.isDetailFieldVisible("unit") &&
        items.some((item) => item.unit)
      ) {
        count += 1;
      }

      if (
        this.isDetailFieldVisible("code") &&
        items.some((item) => item.code)
      ) {
        count += 1;
      }

      if (
        this.isDetailFieldVisible("alternateCode") &&
        items.some((item) => item.barcode)
      ) {
        count += 1;
      }

      if (
        invoiceType !== "CDD" &&
        this.isDetailFieldVisible("discount")
      ) {
        count += 1;
      }

      if (
        !["FEX", "CDD"].includes(invoiceType) &&
        this.isDetailFieldVisible("exemptSales")
      ) {
        count += 1;
      }

      if (
        !["FEX", "CDD"].includes(invoiceType) &&
        this.isDetailFieldVisible("nonSubjectSales")
      ) {
        count += 1;
      }

      if (
        ["FCF", "CCF", "FEX"].includes(invoiceType) &&
        this.isDetailFieldVisible("nonTaxableSales")
      ) {
        count += 1;
      }

      return count;
    },
    detailTableDensityClass() {
      if (this.detailColumnCount <= 7) {
        return "dte-detail-table-section--relaxed";
      }

      if (this.detailColumnCount <= 9) {
        return "dte-detail-table-section--balanced";
      }

      return "dte-detail-table-section--dense";
    },
    guaranteeBusinessId() {
      return (
        this.documentData?.rawData?.branch?.ID_Business ||
        JSON.parse(localStorage.getItem("user"))?.businessid ||
        null
      );
    },
    guaranteeBranchId() {
      return (
        this.documentData?.rawData?.branch?.ID_Branch ||
        JSON.parse(localStorage.getItem("branch"))?.ID_Branch ||
        null
      );
    },
  },
  methods: {
    isDetailFieldVisible(key) {
      return isDteTemplateFieldVisible(this.templateSettings, "detail", key);
    },
    displayPageNumber(pageNumber) {
      return pageNumber + (this.showFullPageBanner ? 1 : 0);
    },
    getPageElements() {
      return Array.from(this.$el.querySelectorAll(".dte-sheet"));
    },
  },
};
</script>
